import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FormControl, FormGroup, Validators } from '@angular/forms';

// Modelos
import { ProductTemp } from '../../../common/models/Product.Model';
import { UserLogin } from '../../../common/models/UserLogin.Model';
import { Container } from '../../../common/models/Container.Model';
import { Params, SingleResponseJson } from 'src/app/ceq/common/models/response';

// Servicios
import { SettingsService } from '../../../../core/settings/settings.service';
import { TaxService } from '../../tax.service';
import { ProxyGet } from 'src/app/ceq/common/services/proxy.service';
import { LabelService } from 'src/app/ceq/enterprise/label/label.service';
import { GlobalFunctionsService } from 'src/app/ceq/common/functions/global-functions.service';

@Component({
  selector: 'app-certify-product-detail',
  templateUrl: './certify-product-detail.component.html',
  styleUrls: ['./certify-product-detail.component.scss']
})
export class CertifyProductDetailComponent implements OnInit {

  FormProductTemp: FormGroup;
  productCode: string = '';
  disabledArt: boolean = false;
  product: ProductTemp = new ProductTemp();
  user: UserLogin;
  containerList: Container[] = [];
  containerList2: Container[] = [];

  constructor(
    private labelService: LabelService,
    private apiService: ProxyGet,
    private settings: SettingsService,
    private taxServices: TaxService,
    private globalFunctionService: GlobalFunctionsService,
    private url: ActivatedRoute
  ) {
    this.user = this.settings.getUser();
    this.productCode = this.url.snapshot.paramMap.get('id') || '';
    this.createForm();
  }

  ngOnInit(): void {
    this.getContainers();
    if (this.productCode) {
      this.loadProductData();
    }
  }

  /**
   * Inicializa el formulario reactivo con validaciones
   */
  createForm() {
    this.FormProductTemp = new FormGroup({
      ProductCode: new FormControl({ value: '', disabled: true }, [Validators.required]),
      ProductDescription: new FormControl({ value: '', disabled: true }, [Validators.required, Validators.maxLength(200)]),
      ProductName: new FormControl({ value: '', disabled: true }, [Validators.required, Validators.maxLength(50)]),
      ItemPlantaId: new FormControl({ value: '', disabled: true }, [Validators.required, Validators.pattern('^[0-9]*$')]),
      ProductType: new FormControl({ value: '', disabled: true }, [Validators.required]),
      ProductPrice: new FormControl({ value: '', disabled: true }, [Validators.required]),
      ProductConversion: new FormControl({ value: '', disabled: true }, [Validators.required]),
      ProductWeight: new FormControl({ value: '', disabled: true }, [Validators.required]),
      ProductContainer: new FormControl({ value: '', disabled: true }, [Validators.required]),
      ProductUom: new FormControl({ value: '', disabled: true }, [Validators.required]),
      ProductLifeDays: new FormControl({ value: '', disabled: true }, [Validators.required])
    });
  }

  /**
   * Obtiene los catálogos para los selectores
   */
  getContainers() {
    this.labelService.getContainers("Contenedor").subscribe(
      (cont: Container[]) => this.containerList = cont,
      async () => await this.globalFunctionService.messageBimbo('Error al obtener Contenedores', 'warning', '¡Alerta!')
    );

    this.labelService.getContainers("Envaces").subscribe(
      (cont: Container[]) => this.containerList2 = cont,
      async () => await this.globalFunctionService.messageBimbo('Error al obtener Envases', 'warning', '¡Alerta!')
    );
  }

  /**
   * Carga la información del producto temporal
   */
  async loadProductData() {
    try {
      const resp = await this.taxServices.getProductTempByCode(this.user.Org, this.productCode).toPromise();
      if (!resp) return;

      this.product = resp;

      // Llenamos el formulario con los datos recibidos
      this.FormProductTemp.patchValue({
        ...resp,
        // Formateamos el precio para la visualización inicial si es necesario
        ProductPrice: resp.ProductPrice 
      });

      if (this.product.Status === 'R') {
        this.disabledArt = true;
        await this.globalFunctionService.messageBimbo("Este producto ya fue removido de los productos temporales", 'warning', '¡Aviso!');
      } else {
        // Habilitamos todos los campos excepto el código que suele ser la llave
        this.FormProductTemp.enable();
        this.FormProductTemp.get('ProductCode')?.disable();
      }
    } catch (error) {
      console.error("Error al cargar producto:", error);
    }
  }

  /**
   * Envía los datos para certificar el producto (Guardar)
   */
  async formSubmit() {
    if (this.FormProductTemp.invalid) {
      await this.globalFunctionService.messageBimbo('Es necesario llenar todos los campos requeridos', 'warning', '¡Alerta!');
      return;
    }

    const rawValues = this.FormProductTemp.getRawValue();
    
    // Mapeo dinámico de parámetros para la API
    const parameters = Object.keys(rawValues).map(key => {
      let val = rawValues[key];
      
      // Limpieza de formatos de moneda o miles si el componente los devuelve como string
      if ((key === 'ProductPrice' || key === 'ProductWeight') && typeof val === 'string') {
        val = val.replace(/[$,]/g, '');
      }

      return {
        Field: key,
        Value: val,
        Object: null
      };
    });

    // Agregamos el barcode manualmente ya que no está en el FormGroup
    parameters.push({ Field: "Codebar", Value: this.product.ProductBarcode, Object: null });

    const request: Params = { Parameter: parameters };

    try {
      const jsonDta = await this.apiService.callAPIWithParams(`Product/updateProductTemp`, "POST", request);
      const response = jsonDta as SingleResponseJson<boolean>;

      if (response && response.Success) {
        await this.globalFunctionService.messageBimbo('El producto fue añadido correctamente', 'success', '¡Éxito!');
        this.btnCancelOnClick();
      } else {
        await this.globalFunctionService.messageBimbo(`${response.Error_code}: ${response.Error_msg}`, 'error', '¡Error!');
      }
    } catch (error) {
      console.error("Error en submit:", error);
    }
  }

  /**
   * Elimina/Rechaza el producto temporal
   */
  async delete() {
    const confirm = await this.globalFunctionService.messageBimbo("¿Desea eliminar el producto del catálogo temporal?", 'question1', 'Confirmación');
    
    if (confirm) {
      const request: Params = {
        Parameter: [
          { Field: "ProductCode", Value: this.FormProductTemp.getRawValue().ProductCode, Object: null }
        ]
      };

      try {
        const jsonDta = await this.apiService.callAPIWithParams(`Product/removeProductTemp`, "PUT", request);
        const response = jsonDta as SingleResponseJson<boolean>;

        if (response && response.Success) {
          await this.globalFunctionService.messageBimbo('El producto fue removido del catálogo', 'success', '¡Éxito!');
          this.btnCancelOnClick();
        } else {
          await this.globalFunctionService.messageBimbo(`${response.Error_code}: ${response.Error_msg}`, 'error', '¡Error!');
        }
      } catch (error) {
        console.error("Error en delete:", error);
      }
    }
  }

  btnCancelOnClick() {
    window.close();
  }
}